/**
 * Life4Earth V2 — React hook for the caller's open Role Rhythm instances.
 * DEV-1145 Phase 1i.
 *
 * Loads pending / due / overdue / snoozed RoleRhythmTaskInstance rows
 * for the current user. Used by the Engagements To-do tab.
 *
 * Pattern follows useApiUserRoles.ts: in-memory queryCache key, refetch
 * exposed for after-mutation refresh.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchMyRoleRhythmInstances } from "./roleRhythms";
import type { RoleRhythmInstanceResponse } from "./roleRhythms";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

export interface UseApiRoleRhythmInstancesResult {
  instances: RoleRhythmInstanceResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading";
  refetch: () => void;
}

const CACHE_KEY = "roleRhythmInstances:mine";

export function useApiRoleRhythmInstances(): UseApiRoleRhythmInstancesResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiRoleRhythmInstancesResult>({
    instances: [],
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const rows = await fetchForKey<RoleRhythmInstanceResponse[]>(
        CACHE_KEY,
        fetchMyRoleRhythmInstances,
        { force: true }
      );
      if (!mountedRef.current) return;
      setData({
        instances: rows,
        loading: false,
        error: null,
        source: rows.length > 0 ? "api" : "empty",
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        source: "empty",
        refetch,
      }));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refetch();

    // Cache-bus invalidation hook: if any code force-clears the cache
    // for our key, re-load. Mirrors useApiUserRoles pattern.
    const onCacheUpdate = (e: Event) => {
      const detail = (e as CustomEvent<{ key: string }>).detail;
      if (detail?.key === CACHE_KEY) void refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
    };
  }, [refetch]);

  return data;
}
